<template>
  <div>
    <BaseDialog max-width="800" persistent :value="true">
      <template #modal>
        <BaseCosmoCard>
          <template #title>
            <div class="modal_header">
              <BaseTitle v-text="$t('utils.observer')" />
              <BaseButtonIcon
                class="close_icon"
                icon="$mdiClose"
                @click="close()"
              />
            </div>
          </template>
          <div class="modal_content">
            <base-autocomplete
              :placeholder="`${$t('utils.search')}...`"
              :loading="collaborators.loading"
              :items="filteredCollaborators"
              :filter="() => true"
              :search-input.sync="collaborators.email"
              item-text="email"
              item-value="id"
              solo
              :menu-props="{ offsetY: true }"
              clearable
              need-selection
            >
              <template #append-item>
                <div v-intersect="loadMoreCollaborators()" />
              </template>
              <template #item="{ item }">
                <div class="colaborator_item">
                  <div class="email" v-text="item.email" />
                  <BaseButton
                    type="primary"
                    small
                    :loading="userToAdd && userToAdd.id == item.id"
                    @click.stop.prevent="followFolder(item)"
                  >
                    {{ $t("utils.add") }}
                  </BaseButton>
                </div>
              </template>
            </base-autocomplete>
            <div class="watchers">
              <div
                v-for="(user, index) in watchers"
                :key="`u${index}`"
                class="watcher_item"
                :class="{
                  bg_secondary: index % 2 !== 0,
                }"
              >
                <div class="avatar_container">
                  <div v-text="user.firstChar" />
                </div>
                <div class="email" v-text="user.email" />
                <v-btn
                  color="grey"
                  class="action"
                  icon
                  :loading="userToRemove && userToRemove.id == user.id"
                  @click="unfollowFolder(user)"
                >
                  <BaseIcon icon="$mdiClose" />
                </v-btn>
              </div>
            </div>
          </div>
        </BaseCosmoCard>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import User from "@/core/Models/User";
import OrganizationAPI from "@/core/Services/organization.api";
import FolderAPI from "@/modules/Folder/Services/folder.api";
export default {
  name: "FolderAssignWatcherDialog",
  props: {
    dialogState: {
      type: Boolean,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    folderId: {
      type: Number,
      required: true,
    },
    watchers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      collaborators: {
        loading: false,
        page: 1,
        items: [],
        pageSize: 10,
        email: undefined,
        searchTimeoutRef: undefined,
      },
      userToAdd: null,
      userToRemove: null,
    };
  },
  computed: {
    filteredCollaborators() {
      return this.collaborators.items
        .filter((item) => !this.watchers.some((w) => w.id === item.user.id))
        .map((c) => new User(c.user));
    },
  },
  created() {
    this.fetchCollaborators();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async fetchCollaborators() {
      if (this.collaborators.loading && !this.collaborators.page) return;
      this.collaborators.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaborators.page,
        this.collaborators.email
      );
      this.collaborators = {
        ...this.collaborators,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.collaborators.loading = false;
    },
    filterCollaborators() {
      this.collaborators.page = 1;
      if (this.collaborators.searchTimeoutRef)
        clearTimeout(this.collaborators.searchTimeoutRef);
      this.collaborators.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 200);
    },
    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaborators.loading ||
        !this.collaborators.next
      )
        return;
      this.collaborators.page = this.collaborators.next;
      this.collaborators.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationCollaborators(
          this.organizationId,
          this.collaborators.page,
          this.collaborators.email
        );
        this.collaborators = {
          items: [...this.collaborators.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.collaborators.loading = false;
    },
    async followFolder(item) {
      this.userToAdd = item;
      try {
        await FolderAPI.postFolderWatcherFollow({
          organizationId: this.organizationId,
          folderId: this.folderId,
          payload: {
            collaborators: [item.id],
          },
        });
        this.$emit("updateWatchers", [...this.watchers, new User(item)]);
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.userToAdd = undefined;
    },
    async unfollowFolder(item) {
      this.userToRemove = item;
      try {
        await FolderAPI.postFolderWatcherUnfollow({
          organizationId: this.organizationId,
          folderId: this.folderId,
          payload: {
            collaborators: [item.id],
          },
        });
        this.$emit(
          "updateWatchers",
          this.watchers.filter((w) => w.id !== item.id)
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.userToRemove = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  margin-top: 24px;
  .watchers {
    display: flex;
    flex-direction: column;
    .watcher_item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;

      .avatar_container {
        border: solid 1px #fff;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #e5e5e5;
        border-radius: 50%;
        font-family: "Inter";
        font-size: 16px;
        line-height: 16px;
        color: #353542;
        font-weight: 600;
      }
      .email {
        flex: 1;
        font-family: "Inter";
        font-size: 14px;
        line-height: 20px;
        color: #707080;
      }
      &.bg_secondary {
        background: #e5e5e5;
        .avatar_container {
          background: #fff;
        }
      }
    }
  }
}
.colaborator_item {
  display: flex;
  align-items: center;
  width: 100%;
  .email {
    flex: 1;
  }
}
</style>
